import { ReactNode, useState } from "react";

import { Classes, Colors, Icon } from "@blueprintjs/core";
import { Link } from "react-router";

import { Box, Flex } from "@/components/layout/flexbox";
import { ReviewHeaderButton } from "@/components/pieces/coaching/reviews/ReviewHeaderButton";
import { getUser } from "@/components/session/JwtTokenManager";
import { STANDARD_APP_MAX_WIDTH, SUBTEXT_COLOR } from "@/css/constants";
import { FullReview } from "@/types/meetings";

import { CancelReviewDialog } from "./CancelReviewDialog";
import { getReviewAction } from "./utils";

interface ReviewHeaderViewProps {
  productName?: string;
  loading: boolean;
  customerId?: string;
  isCoach: boolean;
  action: string;
  menuButton?: ReactNode;
  canceled?: boolean;
}

function ReviewHeaderView({
  productName,
  loading,
  customerId,
  isCoach,
  action,
  menuButton,
  canceled,
}: ReviewHeaderViewProps) {
  return (
    <Box className="bbs" py={[2, 24]} bg={Colors.WHITE}>
      <Box maxWidth={STANDARD_APP_MAX_WIDTH} m="0 auto" px={[3, 24]}>
        <Box mb={3}>
          {isCoach ? (
            <Link to={`/client/${customerId}/reviews`}>
              <Icon icon="chevron-left" size={18} /> Back To Client
            </Link>
          ) : (
            <Link to="/reviews">
              <Icon icon="chevron-left" size={18} /> View All Reviews
            </Link>
          )}
        </Box>
        <Flex justifyContent="space-between" alignItems="center">
          <Box>
            <Box
              fontSize={28}
              fontWeight={600}
              className={loading ? Classes.SKELETON : undefined}
            >
              {productName}
            </Box>
            <Flex
              fontSize={18}
              color={SUBTEXT_COLOR}
              mt={1}
              alignItems="center"
              className={loading ? Classes.SKELETON : undefined}
              style={{
                textDecoration: canceled ? "line-through" : undefined,
              }}
            >
              {action}
            </Flex>
          </Box>
          {menuButton}
        </Flex>
      </Box>
    </Box>
  );
}

interface ReviewHeaderProps {
  review?: FullReview;
  loading: boolean;
}

export function ReviewHeader({ review, loading }: ReviewHeaderProps) {
  const [showCancel, setShowCancel] = useState<boolean>(false);

  if (loading) {
    return (
      <ReviewHeaderView
        loading={true}
        productName="Loading Data"
        canceled={false}
        action="Loading the next action"
        isCoach={false}
      />
    );
  }

  if (!review) return null;

  const isCoach = getUser()?.id === review.coach?.id;
  const redirectUrl = `/reviews`;

  return (
    <>
      <ReviewHeaderView
        isCoach={isCoach}
        customerId={review.customerId?.toString()}
        loading={false}
        productName={review.product?.name}
        menuButton={
          <Box>
            <ReviewHeaderButton
              setShowCancel={setShowCancel}
              review={review}
              isCoach={isCoach}
              label={
                <Box minWidth={24}>
                  <Icon icon="more" color={SUBTEXT_COLOR} size={24} />
                </Box>
              }
            />
          </Box>
        }
        action={getReviewAction(
          review.state || "IN_PROGRESS",
          isCoach,
          review.nextDate,
        )}
        canceled={!!review.canceledAt}
      />
      <CancelReviewDialog
        review={review}
        isCoach={isCoach}
        isOpen={showCancel}
        onCancel={() => setShowCancel(false)}
        onConfirm={() => (window.location.href = redirectUrl)}
        customerId={review.customerId?.toString()}
      />
    </>
  );
}
